import {
  CreatePracticalInformationDtoSubtypeEnum,
  CreatePracticalInformationDtoTypeEnum,
  CreatePracticalInformationFieldDtoFieldTypeEnum,
} from 'src/Services/API/generated';

// Helper functions for UI display
export const getSubtypeLabel = (subtype: CreatePracticalInformationDtoSubtypeEnum): string => {
  // Convert enum value to readable format (replace underscores with spaces and capitalize properly)
  return subtype
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const getCategoryLabel = (category: CreatePracticalInformationDtoTypeEnum): string => {
  // Convert enum value to readable format (replace underscores with spaces and capitalize properly)
  return category
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Helper function to generate a form based on subtype
export const generateFormForSubtype = (subtype: CreatePracticalInformationDtoSubtypeEnum) => {
  const templates = subtypeFieldTemplates[subtype] || [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
    },
  ];
  const icon = subtypeIcons[subtype] || 'info';
  const title = getSubtypeLabel(subtype);

  return {
    title,
    icon,
    fields: templates.map(template => ({
      fieldType: template.fieldType,
      value: '',
      required: true,
    })),
  };
};

// Helper to get valid subtypes for a given category
export const getValidSubtypesForCategory = (
  category: CreatePracticalInformationDtoTypeEnum
): CreatePracticalInformationDtoSubtypeEnum[] | undefined => {
  switch (category) {
    case CreatePracticalInformationDtoTypeEnum.GeneralInformation:
      return [
        CreatePracticalInformationDtoSubtypeEnum.HomeBoatCamperInsurance,
        CreatePracticalInformationDtoSubtypeEnum.GarbageCollection,
        CreatePracticalInformationDtoSubtypeEnum.WasteCollectionPoints,
        CreatePracticalInformationDtoSubtypeEnum.WasteDisposalFacility,
      ];

    case CreatePracticalInformationDtoTypeEnum.SecurityAndAccess:
      return [
        CreatePracticalInformationDtoSubtypeEnum.EmergencyContacts,
        CreatePracticalInformationDtoSubtypeEnum.KeysBadgesAccessCodes,
        CreatePracticalInformationDtoSubtypeEnum.AlarmAndSurveillanceSystem,
        CreatePracticalInformationDtoSubtypeEnum.EmergencyExitsAndEvacuationPlan,
        CreatePracticalInformationDtoSubtypeEnum.FireExtinguishersAndDetectors,
        CreatePracticalInformationDtoSubtypeEnum.FirstAidKitLocation,
        CreatePracticalInformationDtoSubtypeEnum.UtilityMetersAndValvesLocation,
        CreatePracticalInformationDtoSubtypeEnum.EmergencyInstructions,
        CreatePracticalInformationDtoSubtypeEnum.ManualsAndWarrantyLocation,
      ];

    case CreatePracticalInformationDtoTypeEnum.ElectricityAndLighting:
      return [
        CreatePracticalInformationDtoSubtypeEnum.ElectricalInstallationType,
        CreatePracticalInformationDtoSubtypeEnum.TroubleshootingInstructions,
      ];

    case CreatePracticalInformationDtoTypeEnum.WaterAndSanitation:
      return [
        CreatePracticalInformationDtoSubtypeEnum.WaterSupplyType,
        CreatePracticalInformationDtoSubtypeEnum.WaterFiltrationSoftener,
        CreatePracticalInformationDtoSubtypeEnum.WastewaterTankMaintenance,
        CreatePracticalInformationDtoSubtypeEnum.WaterHeaterBoilerOperation,
        CreatePracticalInformationDtoSubtypeEnum.MainPipingLayout,
      ];

    case CreatePracticalInformationDtoTypeEnum.HeatingAndAirConditioning:
      return [
        CreatePracticalInformationDtoSubtypeEnum.HeatingType,
        CreatePracticalInformationDtoSubtypeEnum.ThermostatAndTemperatureControl,
        CreatePracticalInformationDtoSubtypeEnum.AirConditioningAndVentilation,
        CreatePracticalInformationDtoSubtypeEnum.EquipmentMaintenance,
        CreatePracticalInformationDtoSubtypeEnum.GasBottleLocation,
      ];

    case CreatePracticalInformationDtoTypeEnum.InternetAndMultimedia:
      return [
        CreatePracticalInformationDtoSubtypeEnum.InternetProvider,
        CreatePracticalInformationDtoSubtypeEnum.CustomerReferenceContract,
        CreatePracticalInformationDtoSubtypeEnum.WifiCodeAndRouterSetup,
        CreatePracticalInformationDtoSubtypeEnum.AudioSystemConnectedSpeakers,
      ];

    case CreatePracticalInformationDtoTypeEnum.MaintenanceAndStorage:
      return [
        CreatePracticalInformationDtoSubtypeEnum.CleaningProductsAndUsage,
        CreatePracticalInformationDtoSubtypeEnum.VacuumBroomsMop,
        CreatePracticalInformationDtoSubtypeEnum.SensitiveAreasRequiringSpecificMaintenance,
        CreatePracticalInformationDtoSubtypeEnum.GardenEquipment,
      ];

    case CreatePracticalInformationDtoTypeEnum.LeisureAndSpecificEquipment:
      return [
        CreatePracticalInformationDtoSubtypeEnum.SwimmingPool,
        CreatePracticalInformationDtoSubtypeEnum.SpaSauna,
        CreatePracticalInformationDtoSubtypeEnum.TennisCourt,
        CreatePracticalInformationDtoSubtypeEnum.SportsEquipment,
        CreatePracticalInformationDtoSubtypeEnum.FishingOrDivingEquipment,
        CreatePracticalInformationDtoSubtypeEnum.BooksBoardGamesAudiovisualEquipment,
      ];

    case CreatePracticalInformationDtoTypeEnum.Custom:
      return undefined;
    default:
      return [];
  }
};

// Default icons for each category (used in the type Select and suggestions section)
export const typeIcons: Record<CreatePracticalInformationDtoTypeEnum, string> = {
  [CreatePracticalInformationDtoTypeEnum.GeneralInformation]: 'info-circle',
  [CreatePracticalInformationDtoTypeEnum.SecurityAndAccess]: 'shield-lock',
  [CreatePracticalInformationDtoTypeEnum.ElectricityAndLighting]: 'bulb',
  [CreatePracticalInformationDtoTypeEnum.WaterAndSanitation]: 'droplet',
  [CreatePracticalInformationDtoTypeEnum.HeatingAndAirConditioning]: 'flame',
  [CreatePracticalInformationDtoTypeEnum.InternetAndMultimedia]: 'wifi',
  [CreatePracticalInformationDtoTypeEnum.MaintenanceAndStorage]: 'tool',
  [CreatePracticalInformationDtoTypeEnum.LeisureAndSpecificEquipment]: 'ball-tennis',
  [CreatePracticalInformationDtoTypeEnum.Custom]: 'tag',
};

// Default icons for each subtype
export const subtypeIcons: Record<CreatePracticalInformationDtoSubtypeEnum, string> = {
  // General Information
  [CreatePracticalInformationDtoSubtypeEnum.HomeBoatCamperInsurance]: 'shield',
  [CreatePracticalInformationDtoSubtypeEnum.GarbageCollection]: 'trash',
  [CreatePracticalInformationDtoSubtypeEnum.WasteCollectionPoints]: 'recycle',
  [CreatePracticalInformationDtoSubtypeEnum.WasteDisposalFacility]: 'truck',

  // Security and Access
  [CreatePracticalInformationDtoSubtypeEnum.EmergencyContacts]: 'phone-call',
  [CreatePracticalInformationDtoSubtypeEnum.KeysBadgesAccessCodes]: 'key',
  [CreatePracticalInformationDtoSubtypeEnum.AlarmAndSurveillanceSystem]: 'shield-lock',
  [CreatePracticalInformationDtoSubtypeEnum.EmergencyExitsAndEvacuationPlan]: 'door-exit',
  [CreatePracticalInformationDtoSubtypeEnum.FireExtinguishersAndDetectors]: 'flame',
  [CreatePracticalInformationDtoSubtypeEnum.FirstAidKitLocation]: 'first-aid-kit',
  [CreatePracticalInformationDtoSubtypeEnum.UtilityMetersAndValvesLocation]: 'gauge',
  [CreatePracticalInformationDtoSubtypeEnum.EmergencyInstructions]: 'alert-triangle',
  [CreatePracticalInformationDtoSubtypeEnum.ManualsAndWarrantyLocation]: 'file-text',

  // Electricity and Lighting
  [CreatePracticalInformationDtoSubtypeEnum.ElectricalInstallationType]: 'plug',
  [CreatePracticalInformationDtoSubtypeEnum.TroubleshootingInstructions]: 'tool',

  // Water and Sanitation
  [CreatePracticalInformationDtoSubtypeEnum.WaterSupplyType]: 'droplet',
  [CreatePracticalInformationDtoSubtypeEnum.WaterFiltrationSoftener]: 'filter',
  [CreatePracticalInformationDtoSubtypeEnum.WastewaterTankMaintenance]: 'barrel',
  [CreatePracticalInformationDtoSubtypeEnum.WaterHeaterBoilerOperation]: 'teapot',
  [CreatePracticalInformationDtoSubtypeEnum.MainPipingLayout]: 'layout',

  // Heating and Air Conditioning
  [CreatePracticalInformationDtoSubtypeEnum.HeatingType]: 'flame',
  [CreatePracticalInformationDtoSubtypeEnum.ThermostatAndTemperatureControl]: 'temperature',
  [CreatePracticalInformationDtoSubtypeEnum.AirConditioningAndVentilation]: 'air-conditioning',
  [CreatePracticalInformationDtoSubtypeEnum.EquipmentMaintenance]: 'tool',
  [CreatePracticalInformationDtoSubtypeEnum.GasBottleLocation]: 'gas-station',

  // Internet and Multimedia
  [CreatePracticalInformationDtoSubtypeEnum.InternetProvider]: 'world',
  [CreatePracticalInformationDtoSubtypeEnum.CustomerReferenceContract]: 'id',
  [CreatePracticalInformationDtoSubtypeEnum.WifiCodeAndRouterSetup]: 'wifi',
  [CreatePracticalInformationDtoSubtypeEnum.AudioSystemConnectedSpeakers]: 'device-speaker',

  // Maintenance and Storage
  [CreatePracticalInformationDtoSubtypeEnum.CleaningProductsAndUsage]: 'spray',
  [CreatePracticalInformationDtoSubtypeEnum.VacuumBroomsMop]: 'vacuum-cleaner',
  [CreatePracticalInformationDtoSubtypeEnum.SensitiveAreasRequiringSpecificMaintenance]:
    'alert-hexagon',
  [CreatePracticalInformationDtoSubtypeEnum.GardenEquipment]: 'plant',

  // Leisure and Specific Equipment
  [CreatePracticalInformationDtoSubtypeEnum.SwimmingPool]: 'pool',
  [CreatePracticalInformationDtoSubtypeEnum.SpaSauna]: 'bath',
  [CreatePracticalInformationDtoSubtypeEnum.TennisCourt]: 'ball-tennis',
  [CreatePracticalInformationDtoSubtypeEnum.SportsEquipment]: 'barbell',
  [CreatePracticalInformationDtoSubtypeEnum.FishingOrDivingEquipment]: 'fish',
  [CreatePracticalInformationDtoSubtypeEnum.BooksBoardGamesAudiovisualEquipment]: 'device-tv',
};

// Define field template interface
export interface FieldTemplate {
  fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum;
  multiline?: boolean;
  key: string;
}

// Default field templates for each subtype
export const subtypeFieldTemplates: Record<
  CreatePracticalInformationDtoSubtypeEnum,
  FieldTemplate[]
> = {
  // General Information subtypes
  [CreatePracticalInformationDtoSubtypeEnum.HomeBoatCamperInsurance]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'insurance_company',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'policy_number',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'coverage_details',
      multiline: true,
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'membership_fee',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'insured_value',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Date,
      key: 'renewal_date',
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.GarbageCollection]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'bin_type',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'color',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Schedule,
      key: 'collection_days',
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.WasteCollectionPoints]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'waste_type',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Location,
      key: 'address',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'opening_hours',
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.WasteDisposalFacility]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Location,
      key: 'facility_address',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'opening_hours',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'accepted_waste_types',
      multiline: true,
    },
  ],

  // Security and Access subtypes
  [CreatePracticalInformationDtoSubtypeEnum.EmergencyContacts]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'emergency_services',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'local_police',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'local_hospital',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'property_manager',
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.KeysBadgesAccessCodes]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'door_description',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Password,
      key: 'access_code',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'keys_badges_location',
      multiline: true,
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.AlarmAndSurveillanceSystem]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'system_type',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'system_brand',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Password,
      key: 'alarm_code',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'usage_instructions',
      multiline: true,
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.EmergencyExitsAndEvacuationPlan]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'emergency_exits_location',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'evacuation_instructions',
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.FireExtinguishersAndDetectors]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'extinguishers_location',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'detectors_location',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Date,
      key: 'next_check_date',
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.FirstAidKitLocation]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'first_aid_kit_location',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'kit_contents',
      multiline: true,
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Date,
      key: 'replacement_date',
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.UtilityMetersAndValvesLocation]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'electricity_meter_location',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'water_valve_location',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'gas_valve_location',
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.EmergencyInstructions]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'fire_instructions',
      multiline: true,
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'water_leak_instructions',
      multiline: true,
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'power_outage_instructions',
      multiline: true,
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.ManualsAndWarrantyLocation]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'manuals_location',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'warranty_location',
    },
  ],

  // Electricity and Lighting subtypes
  [CreatePracticalInformationDtoSubtypeEnum.ElectricalInstallationType]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'installation_type',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'panel_location',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'other_panel_location',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'circuit_breaker_info',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'fuse_information',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'subscribed_power',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'electricity_provider',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'additional_details',
      multiline: true,
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.TroubleshootingInstructions]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'outage_instructions',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'maintenance_contact',
    },
  ],

  // Water and Sanitation subtypes
  [CreatePracticalInformationDtoSubtypeEnum.WaterSupplyType]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'water_supply_type',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'meter_location',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'main_valve_location',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'drainage_type',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'additional_details',
      multiline: true,
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.WaterFiltrationSoftener]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'filtration_softener_type',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'maintenance_instructions',
      multiline: true,
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Date,
      key: 'filtration_softener_next_filter_change_date',
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.WastewaterTankMaintenance]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'emptying_instructions',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'maintenance_instructions',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Date,
      key: 'next_emptying_date',
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.WaterHeaterBoilerOperation]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'water_heater_type',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'water_heater_brand',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'water_heater_model',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'water_heater_location',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'usage_instructions',
      multiline: true,
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Date,
      key: 'next_check_date',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'maintenance_instructions',
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.MainPipingLayout]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'piping_layout_description',
    },
  ],

  // Heating and Air Conditioning subtypes
  [CreatePracticalInformationDtoSubtypeEnum.HeatingType]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'heating_type',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'usage_instructions',
      multiline: true,
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.ThermostatAndTemperatureControl]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'thermostat_location',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'regulation_instructions',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'other_thermostat_location',
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.AirConditioningAndVentilation]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'system_type',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'system_brand',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'system_model',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Date,
      key: 'next_check_date',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'usage_instructions',
      multiline: true,
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.EquipmentMaintenance]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'maintenance_instructions',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'recommended_maintenance_frequency',
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.GasBottleLocation]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'gas_bottles_location',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'replacement_instructions',
    },
  ],

  // Internet and Multimedia subtypes
  [CreatePracticalInformationDtoSubtypeEnum.InternetProvider]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'provider_name',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'subscribed_speed',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'support_contact',
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.CustomerReferenceContract]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'customer_reference',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'contract_number',
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.WifiCodeAndRouterSetup]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'network_name',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Password,
      key: 'wifi_password',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'router_location',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'router_setup',
      multiline: true,
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'admin_url',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Password,
      key: 'admin_password',
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.AudioSystemConnectedSpeakers]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'audio_system_type',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'usage_instructions',
      multiline: true,
    },
  ],

  // Maintenance and Storage subtypes
  [CreatePracticalInformationDtoSubtypeEnum.CleaningProductsAndUsage]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'available_products',
      multiline: true,
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'available_products_category',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'available_products_location',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'usage_instructions',
      multiline: true,
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.VacuumBroomsMop]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'vacuum_location',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'brooms_mops_location',
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.SensitiveAreasRequiringSpecificMaintenance]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'sensitive_areas_description',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'specific_maintenance_instructions',
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.GardenEquipment]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'available_equipment',
      multiline: true,
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'water_jets_instructions',
      multiline: true,
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'automatic_watering_instructions',
      multiline: true,
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'outdoor_lighting_instructions',
      multiline: true,
    },
  ],

  // Leisure and Specific Equipment subtypes
  [CreatePracticalInformationDtoSubtypeEnum.SwimmingPool]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'brand_and_customer_reference',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'dimensions',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'filtration_type',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'maintenance_instructions',
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.SpaSauna]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'brand_and_reference',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'usage_instructions',
      multiline: true,
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'maintenance_instructions',
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.TennisCourt]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'surface_type',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'maintenance_provider',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'customer_reference',
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.SportsEquipment]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.List,
      key: 'available_equipment',
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.FishingOrDivingEquipment]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'available_equipment',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'usage_instructions',
      multiline: true,
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'local_restrictions',
    },
  ],
  [CreatePracticalInformationDtoSubtypeEnum.BooksBoardGamesAudiovisualEquipment]: [
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'available_books',
      multiline: true,
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'available_books_location',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'available_board_games',
      multiline: true,
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'available_board_games_location',
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'audiovisual_equipment',
      multiline: true,
    },
    {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      key: 'audiovisual_equipment_location',
    },
  ],
};

export const getFieldTemplate = (key: string) => {
  if (key === 'custom') {
    return {
      fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
      multiline: true,
      key: 'custom',
    };
  }
  const subtypes = Object.values(CreatePracticalInformationDtoSubtypeEnum);

  for (const subtype of subtypes) {
    const templates = subtypeFieldTemplates[subtype];

    const foundTemplate = templates.find(template => template.key === key);

    if (foundTemplate) {
      return foundTemplate;
    }
  }

  return undefined;
};
